import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Eye, Heart } from 'lucide-react';
import { getCategoryBySlug, getWorks, getMediaUrl } from '../services/api';
import VideoModal from './VideoModal';

const buildFilterConfig = (filters) => {
  if (Array.isArray(filters)) {
    const tabs = filters.length > 0 ? filters : ['全部'];
    return { tabs, allLabel: tabs[0], featuredLabel: tabs.includes('推荐') ? '推荐' : null };
  }
  if (!filters) return { tabs: ['全部'], allLabel: '全部', featuredLabel: null };

  const systemTabs = Object.values(filters.system || {})
    .filter(item => item?.enabled)
    .map(item => item.label)
    .filter(Boolean);
  const customTabs = (filters.custom || [])
    .filter(item => item?.enabled)
    .map(item => item.name)
    .filter(Boolean);

  const tabs = [...new Set([...systemTabs, ...customTabs])];
  const allLabel = filters.system?.all?.enabled
    ? filters.system.all.label
    : tabs[0] || '全部';
  return {
    tabs: tabs.length > 0 ? tabs : ['全部'],
    allLabel,
    featuredLabel: filters.system?.featured?.enabled ? filters.system.featured.label : null
  };
};

const CategoryVideoList = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('全部');
  const [categoryConfig, setCategoryConfig] = useState(null);
  const [videos, setVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);

  // 从API加载分类配置和作品列表
  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        // 获取分类信息
        const category = await getCategoryBySlug(categoryId);

        if (category) {
          const filterConfig = buildFilterConfig(category.filters);
          setCategoryConfig({
            categoryId: category.slug,
            categoryName: category.name,
            banner: {
              ...(category.banner || {}),
              backgroundImage: getMediaUrl(category.banner?.backgroundImage),
              overlay: category.banner?.overlay || 'dark'
            },
            filters: filterConfig.tabs,
            allLabel: filterConfig.allLabel,
            featuredLabel: filterConfig.featuredLabel
          });
          setActiveTab(filterConfig.allLabel);

          // 获取该分类下的作品
          const worksData = await getWorks({
            category: category._id,
            limit: 100
          });
          setVideos((worksData.works || []).map(video => ({
            ...video,
            id: video.id || video._id,
            cover: getMediaUrl(video.cover || video.coverImage),
            videoUrl: video.mediaType === 'video'
              ? getMediaUrl(video.videoUrl || video.mediaFiles?.[0])
              : '',
            tagNames: (video.tags || []).map(tag => typeof tag === 'string' ? tag : tag.name)
          })));
        } else {
          // 分类不存在，跳转回首页
          navigate('/');
        }
      } catch (error) {
        console.error('❌ 获取分类数据失败:', error);
        navigate('/');
      }
    };

    fetchCategoryData();
  }, [categoryId, navigate]);

  // 筛选视频
  const filteredVideos = activeTab === categoryConfig?.allLabel
    ? videos
    : activeTab === categoryConfig?.featuredLabel
      ? videos.filter(video => video.isFeatured)
      : videos.filter(video => video.tagNames?.includes(activeTab));

  if (!categoryConfig) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500">加载中...</p>
    </div>;
  }

  const { banner, categoryName, filters } = categoryConfig;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner - 全屏图片 + 渐变遮罩 */}
      <div className="relative w-full h-[45vh] overflow-hidden">
        {/* 背景图片 */}
        {banner.backgroundImage ? (
          <img
            src={banner.backgroundImage}
            alt={categoryName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-900" />
        )}

        {/* 渐变遮罩 */}
        <div className={`absolute inset-0 ${
          banner.overlay === 'dark'
            ? 'bg-gradient-to-b from-black/60 via-black/40 to-black/70'
            : 'bg-gradient-to-b from-black/40 via-black/20 to-black/50'
        }`} />

        {/* 返回按钮 */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 bg-black/30 backdrop-blur-sm rounded-full p-2 hover:bg-black/50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        {/* 标题内容 */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <h1 className="text-3xl font-bold mb-2 tracking-wide">{categoryName}</h1>
          {banner.subtitle && (
            <p className="text-sm text-white/80">{banner.subtitle}</p>
          )}
        </div>
      </div>

      {/* 筛选Tab */}
      {filters && filters.length > 0 && (
        <div className="sticky top-0 bg-white shadow-sm z-10">
          <div className="px-4 py-3">
            <div className="flex gap-6 overflow-x-auto scrollbar-hide">
              {filters.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`relative pb-2 whitespace-nowrap transition-all ${
                    activeTab === tab
                      ? 'text-black text-base font-medium'
                      : 'text-gray-500 text-sm hover:text-gray-700'
                  }`}
                >
                  {tab}
                  {/* Underline */}
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-black transition-all duration-300 ${
                    activeTab === tab ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                  }`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 视频列表 - 瀑布流布局 */}
      <div className="px-4 py-4">
        {filteredVideos.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400">暂无视频</p>
          </div>
        ) : (
          <div className="columns-2 gap-3">
            {filteredVideos.map((video) => (
              <div key={video.id} className="bg-white rounded-xl overflow-hidden shadow-sm mb-3 break-inside-avoid">
                <div className="relative">
                  <img
                    src={video.cover}
                    alt={video.title}
                    className="w-full aspect-[3/4] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

                  {/* Play Button */}
                  <button
                    type="button"
                    onClick={() => video.videoUrl && setPlayingVideo(video)}
                    className="absolute inset-0 flex items-center justify-center group"
                    aria-label={`播放 ${video.title}`}
                  >
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all">
                      <Play className="w-6 h-6 text-white fill-white" />
                    </div>
                  </button>

                  {/* Duration Badge */}
                  <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </span>
                </div>

                <div className="p-3">
                  <h3 className="text-sm font-medium line-clamp-2 text-left mb-2">{video.title}</h3>
                  {/* Stats */}
                  <div className="flex items-center gap-3 text-gray-500 text-xs">
                    {video.views !== undefined && <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {video.views}
                    </span>}
                    {video.likes !== undefined && <span className="flex items-center gap-1">
                      <Heart className="w-3 h-3" />
                      {video.likes}
                    </span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部加载更多提示 */}
      {filteredVideos.length > 0 && (
        <div className="text-center py-8 text-xs text-gray-400">
          已加载全部内容
        </div>
      )}

      <VideoModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
    </div>
  );
};

export default CategoryVideoList;
